import { Component, Inject } from "@angular/core";
import { VNG_DIALOG_DATA, VngDialogRef } from "@vengreso/angular-components";
import { Router } from "@angular/router";
import { get } from "lodash";
import { PlansConstants, getCategoriesReward, getPlanId } from "src/app/core/core-services/constants/plans.constants";
import { PlanPricingService, YearlyPlanPricing } from "src/app/core/core-services/services/plan-pricing.service";
import { formatCents } from "src/app/utils/price-format.utils";

const DEFAULT_UPGRADE_TITLE =
  "You’ve reached the limits of your current plan. \nPlease upgrade your subscription.";

interface WarningCopy {
  title: string;
  /** Overrides `title` when the current plan is Starter. */
  starterTitle?: string;
  message: string;
  starterPlanDesc?: string[];
  growthPlanDesc?: string[];
  salesProPlanDesc?: string[];
  /** Overrides the Growth card's button label. */
  upgrade?: string;
}

const warnings = {
  fly_grammar: {
    title: DEFAULT_UPGRADE_TITLE,
    message: "You've unlocked a premium feature, upgrade now.",
    salesProPlanDesc: ["Disable FlyGrammar globally"],
    growthPlanDesc: ["Disable FlyGrammar globally"],
  },
  persona: {
    title: DEFAULT_UPGRADE_TITLE,
    message: "You've unlocked a premium feature, upgrade now.",
    salesProPlanDesc: ["7 Personas", "Unlimited AI Emulation", "Copy AI Emulation"],
    growthPlanDesc: ["3 Personas"],
  },
  editor: {
    title: DEFAULT_UPGRADE_TITLE,
    starterTitle: DEFAULT_UPGRADE_TITLE,
    message: "Select which plan fits your needs!",
    starterPlanDesc: ["Most Text Editor Functions", "Add Images"],
    growthPlanDesc: ["All Text Editor Functions", "Add Any Media Type"],
  },
  images: {
    title: DEFAULT_UPGRADE_TITLE,
    starterTitle: DEFAULT_UPGRADE_TITLE,
    message: "Select which plan fits your needs!",
    starterPlanDesc: ["Image upload size up to 5MB"],
    growthPlanDesc: ["Image upload size up to 20MB"],
  },
  editorFonts: {
    title: DEFAULT_UPGRADE_TITLE,
    starterTitle: DEFAULT_UPGRADE_TITLE,
    message: "Select which plan fits your needs!",
    starterPlanDesc: ["3 Fonts"],
    growthPlanDesc: ["Unlimited Fonts"],
  },
  numOfFlycuts: {
    title: DEFAULT_UPGRADE_TITLE,
    message: "Select which plan fits your needs!",
    starterPlanDesc: ["300 FlyCuts"],
    growthPlanDesc: ["Unlimited FlyCuts"],
  },
  numOfChar: {
    title: DEFAULT_UPGRADE_TITLE,
    message: "Select which plan fits your needs!",
    starterPlanDesc: ["25,000 Characters"],
    growthPlanDesc: ["Unlimited Characters"],
  },
  flyLearningWarning: {
    title: DEFAULT_UPGRADE_TITLE,
    message: "Your current plan doesn't have access to On Demand content",
    salesProPlanDesc: [
      "Unlimited On-Demand Access",
      "14+ Hours of Prospecting Training",
      "50+ Prospecting Scripts",
      "13 Messaging Playbooks",
    ],
  },
  advancedSearch: {
    title: DEFAULT_UPGRADE_TITLE,
    message: "",
    starterPlanDesc: ["Advanced Search"],
    growthPlanDesc: ["Advanced Search"],
  },
  import: {
    title: "You’ve discovered some features that are only available with the Starter and/or Growth plan. ",
    message: "Please upgrade your plan to import all your FlyCuts.",
    starterPlanDesc: [
      "• Up to 300 FlyCuts",
      "• 25,000 Characters per FlyCut",
      "• Up to 7 Categories & 14 Subcategories",
      "• Rich text editor",
    ],
    growthPlanDesc: [
      "• Unlimited FlyCuts",
      "• Unlimited Characters Per FlyCut",
      "• Unlimited Categories",
      "• Rich text editor",
    ],
  },
  categories: {
    title: "You’ve reached the limits of your current plan. Please upgrade your subscription.",
    message: "Select which plan fits your needs!",
    starterPlanDesc: ["7 Categories"],
    growthPlanDesc: ["Unlimited Categories"],
  },
  Flyplates: {
    title: "You’ve reached the limits of your current plan. Please upgrade your subscription.",
    starterTitle: "You’ve reached the limits of your current plan. Please upgrade your subscription.",
    message: "",
    starterPlanDesc: ["Up to 50 FlyPlates"],
    growthPlanDesc: ["Unlimited FlyPlates"],
  },
  versionHistory: {
    title: "You need to upgrade to Starter or Growth Plan to use the version history",
    starterTitle: "You need to upgrade to Starter or Growth Plan to use the version history",
    message: "Select which plan fits your needs!",
    starterPlanDesc: ["Leverage 3 of the Latest Versions"],
    growthPlanDesc: ["Leverage up to 999 Versions"],
  },
  versionHistoryForStarter: {
    title: "Upgrade to Growth Plan to restore any of the 999 versions",
    starterTitle: "Upgrade to Growth Plan to restore any of the 999 versions",
    message: "Select which plan fits your needs!",
    starterPlanDesc: [
      "Unlimited FlyCuts",
      "Unlimited Characters Per FlyCut",
      "Unlimited Categories",
      "Rich text editor",
    ],
    growthPlanDesc: [
      "Unlimited FlyCuts",
      "Unlimited Characters Per FlyCut",
      "Unlimited Categories",
      "Rich text editor",
    ],
  },
  FlyplatesForStarter: {
    title: "Upgrade your account to add more FlyPlates",
    message: "",
    starterPlanDesc: [""],
    growthPlanDesc: ["Unlimited FlyPlates"],
    upgrade: "Upgrade to Growth",
  },
  pasteFormatting: {
    title: "Your pasted content contains formatting not included in your plan.",
    starterTitle: "Your pasted content contains formatting not included in your plan.",
    message: "Upgrade to keep the formatting, or dismiss to paste without formatting.",
    starterPlanDesc: ["Most Text Editor Functions", "Add Images"],
    growthPlanDesc: ["All Text Editor Functions", "Add Any Media Type"],
  },
} satisfies Record<string, WarningCopy>;

/** The upsell variant to show — keys into the `warnings` copy table. */
export type WarningType = keyof typeof warnings;

/** Payload for {@link WarningDialogComponent}. */
export interface WarningDialogData {
  type: WarningType;
  /**
   * Restriction key (or TinyMCE command name) resolved to a human message
   * via ERROR_KEY_PATHS; unknown keys are shown as-is.
   */
  error?: string;
  /** Pre-resolved message shown instead of the plan cards (template reads it directly). */
  errorMessage?: string;
  /** True to show the Starter card next to Growth; false hides the message line. */
  showTheStarter?: boolean;
  /** "salesPro" switches the layout to the single SalesPro card. */
  selectedPlan?: string;
  /**
   * `{token}` substitutions applied to the resolved `errorMsg` (issue #569:
   * the daily-prompt-quota-exhausted copy needs the live total/countdown, and
   * the `errors` dictionary below only holds static strings).
   */
  messageParams?: Record<string, string | number>;
}

const errors = {
  editor: {
    generic: "Your current plan does not allow you to use this feature",
    blockStyle: "Your current plan does not allow the block styling feature within a FlyMSG",
    italic: "Your current plan does not allow the italic feature within FlyMSG",
    strikethrough: "Your current plan does not allow the strikethrough feature within FlyMSG",
    underline: "Your current plan does not allow the underline feature within FlyMSG",
    bulletPoint: "Your current plan does not allow the bullet point feature within a FlyMSG",
    numberedList: "Your current plan does not allow the numbered list feature within a FlyMSG",
    preview: "Your current plan does not allow the preview feature within a FlyMSG",
    hyperlink: "Your current plan does not allow to hyperlink text within a FlyMSG",
    clearFormatting: "Your current plan does not allow the clear formatting feature within a FlyMSG",
    centerAlignment: "Your current plan does not allow the center-alignment feature within a FlyMSG",
    rightAlignment: "Your current plan does not allow the right-alignment feature within a FlyMSG",
    leftAlignment: "Your current plan does not allow the left-alignment feature within a FlyMSG",
    increaseIndent: "Your current plan does not allow to increase indent within a FlyMSG",
    decreaseIndent: "Your current plan does not allow to decrease indent within a FlyMSG",
    fontColor: "Your current plan does not allow to change font color within a FlyMSG",
    backgroundColor: "Your current plan does not allow you to change background color within a FlyMSG",
    fontType: "Your current plan does not allow to change font type within a FlyMSG",
    fontSize: "Your current plan does not allow to change font size within a FlyMSG",
    spellchecker: "Your current plan does not allow you to check spelling for any language except English",
    pasteFormatting: "Your pasted content contains formatting not available in your current plan",
  },
  flycut: {
    maxFree: "Your current plan allows a limit of 20 FlyCuts",
    maxStarter: "Your current plan allows a limit of 300 FlyCuts",
    maxCharFree: "Your current plan allows a limit of 2,500 characters per FlyMSG",
    maxCharStarter: "Your current plan allows a limit of 25,000 characters per FlyMSG",
  },
  advancedSearch: {
    advancedSearch: "Your current plan does not have access to advanced search.",
  },
  categories: {
    maxFree: `Your current plan allows a limit of ${PlansConstants.FREE.CATEGORIES + getCategoriesReward()} categories`,
    maxStarter: `Your current plan allows a limit of ${
      PlansConstants.STARTER.CATEGORIES + getCategoriesReward()
    } categories`,
  },
  subCategories: {
    maxFree: "Your current plan allows a limit of 14 subcategories",
    maxStarter: "Your current plan allows a limit of 28 subcategories",
  },
  flyplates: {
    maxFree: "Your current plan allows you access only to FlyPlates in the GENERAL category",
    maxStarter: "Your current plan allows a limit of 50 FlyPlates from any category",
  },
  images: {
    maxFree: "Your current plan does not allow you to upload an image larger than 1MB",
    maxStarter: "Your current plan does not allow you to upload an image larger than 5MB",
  },
  persona: {
    maxNumberOfPersonas: "You've unlocked a premium feature, upgrade now.",
    maxNumberOfPersonasFree: "Your current plan does not allow more than 1 persona",
    maxNumberOfPersonasGrowth: "Your current plan does not allow more than 3 persona",
    maxNumberOfPersonasPro: "Your current plan does not allow more than 7 persona",
    // Issue #569: Freemium/Starter/Growth get a 403 from the backend on every
    // Regenerate (AddFeaturesPlanSeeder seeds regenerate_count = 0 for those
    // plans) — this replaces the misleading maxNumberOfPersonas ("premium
    // feature") copy that used to show for this case regardless of plan.
    regenerateNotInPlan:
      "Regenerating an AI Persona isn't included in your plan. Upgrade to Business Pro to regenerate as often as you like.",
    // {total}/{countdown} are filled in from WarningDialogData.messageParams
    // at open time — the live quota total and the seconds-until-refill
    // countdown aren't static copy.
    dailyPromptQuotaExhausted:
      "You've used all {total} AI prompts for today. Your quota refills in {countdown}. Upgrade for more prompts per day.",
  },
  fly_grammar: {
    enableDisableFlyGrammar: "Your current plan does not allow you to disable FlyGrammar globally.",
    disableFlyGrammarPerSite:
      "Your current plan does not allow you to turn FlyGrammar off, even for a single site.",
  },
};

/**
 * Maps a restriction key (or raw TinyMCE command name, e.g. "JustifyCenter")
 * to its message path in `errors`. Replaces the former 40-case switch.
 */
const ERROR_KEY_PATHS: Record<string, string> = {
  blockStyle: "editor.blockStyle",
  italic: "editor.italic",
  strikethrough: "editor.strikethrough",
  underline: "editor.underline",
  InsertUnorderedList: "editor.bulletPoint",
  InsertOrderedList: "editor.numberedList",
  mcePreview: "editor.preview",
  mceLink: "editor.hyperlink",
  RemoveFormat: "editor.clearFormatting",
  JustifyCenter: "editor.centerAlignment",
  JustifyFull: "editor.centerAlignment",
  JustifyRight: "editor.rightAlignment",
  JustifyLeft: "editor.leftAlignment",
  indent: "editor.increaseIndent",
  outdent: "editor.decreaseIndent",
  fontColor: "editor.fontColor",
  backgroundColor: "editor.backgroundColor",
  FontName: "editor.fontType",
  FontSize: "editor.fontSize",
  spellchecker: "editor.spellchecker",
  pasteFormatting: "editor.pasteFormatting",
  maxFree: "flycut.maxFree",
  maxStarter: "flycut.maxStarter",
  maxCharFree: "flycut.maxCharFree",
  maxCharStarter: "flycut.maxCharStarter",
  advancedSearch: "advancedSearch.advancedSearch",
  categoriesFree: "categories.maxFree",
  categoriesStarter: "categories.maxStarter",
  subCategoriesFree: "subCategories.maxFree",
  subCategoriesStarter: "subCategories.maxStarter",
  flyplatesFree: "flyplates.maxFree",
  flyplatesStarter: "flyplates.maxStarter",
  imagesFree: "images.maxFree",
  imagesStarter: "images.maxStarter",
  maxNumberOfPersonas: "persona.maxNumberOfPersonas",
  maxNumberOfPersonasFree: "persona.maxNumberOfPersonasFree",
  maxNumberOfPersonasGrowth: "persona.maxNumberOfPersonasGrowth",
  maxNumberOfPersonasPro: "persona.maxNumberOfPersonasPro",
  regenerateNotInPlan: "persona.regenerateNotInPlan",
  dailyPromptQuotaExhausted: "persona.dailyPromptQuotaExhausted",
  enableDisableFlyGrammar: "fly_grammar.enableDisableFlyGrammar",
  disableFlyGrammarPerSite: "fly_grammar.disableFlyGrammarPerSite",
};

/**
 * The app-wide "upgrade your plan" upsell dialog.
 *
 * One component with data-driven variants (see {@link WarningDialogData}):
 * `type` picks the copy, `error` adds the specific restriction message,
 * `selectedPlan`/`showTheStarter` pick which plan cards render.
 *
 * Closes with `'upgraded'` (navigates to /new-plan/...) or `'dismissed'`.
 */
@Component({
  selector: "app-warning-dialog",
  templateUrl: "./warning-dialog.component.html",
  styleUrls: ["./warning-dialog.component.scss"],
  standalone: false,
})
export class WarningDialogComponent {
  title: string;
  message: string;
  starterPlanDesc: string[];
  growthPlanDesc: string[];
  salesProPlanDesc: string[];
  showTheStarter: boolean;
  errorMsg: string;
  upgrade: string;
  pricing: YearlyPlanPricing = {};
  selectedPlan: string;

  constructor(
    public dialogRef: VngDialogRef<WarningDialogComponent, "dismissed" | "upgraded">,
    private readonly router: Router,
    @Inject(VNG_DIALOG_DATA) public data: WarningDialogData,
    private readonly planPricingService: PlanPricingService
  ) {
    this.selectedPlan = data.selectedPlan;
    this.planPricingService.getYearlyPlanPricing().subscribe((pricing) => (this.pricing = pricing));

    const copy: WarningCopy = warnings[data.type] ?? ({} as WarningCopy);
    this.title = getPlanId() == "Starter" && copy.starterTitle ? copy.starterTitle : copy.title;
    this.message = data.showTheStarter == false ? "" : copy.message;
    this.starterPlanDesc = copy.starterPlanDesc;
    this.growthPlanDesc = copy.growthPlanDesc;
    this.salesProPlanDesc = copy.salesProPlanDesc;
    this.errorMsg = get(errors, ERROR_KEY_PATHS[data.error] ?? "") ?? data.error;
    if (this.errorMsg && data.messageParams) {
      this.errorMsg = Object.entries(data.messageParams).reduce(
        (msg, [token, value]) => msg.replace(new RegExp(`\\{${token}\\}`, "g"), String(value)),
        this.errorMsg
      );
    }
    if (data.error == "spellchecker") {
      this.starterPlanDesc = ["Most Text Editor Functions", "Add Images"];
      this.growthPlanDesc = ["Most Text Editor Functions", "Add Any Media Type"];
    }
    this.showTheStarter = data.showTheStarter;
    this.upgrade = copy.upgrade;
  }

  cancel(): void {
    this.dialogRef.close("dismissed");
  }

  navigateTo(plan: string): void {
    const routes: Record<string, string> = {
      starter: "/new-plan/YearlyStarter",
      growth: "/new-plan/YearlyGrowth",
      "sales-pro": "/new-plan/YearlySalesPro",
    };
    const route = routes[plan];
    if (route) {
      this.dialogRef.close("upgraded");
      this.router.navigate([route]);
    }
  }

  // Issue #607: delegates to the shared cents formatter (this call site
  // already took cents in and rounded correctly; this only removes its
  // private copy of the arithmetic).
  displayedPrice(amount: number | undefined): string {
    return formatCents(amount);
  }
}
